import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
} from 'react-native';
import DLOModal from './DLOModal';
import DLODropdown from './DLODropdown';
import { DLODropdownOption } from '../types/dlo.types';
import { getWeekOptions } from '../../../utils/filterOptions';

/**
 * DLOCreateScheduleModal Component
 * 
 * Features implemented:
 * 1. Year dropdown showing current year and next year
 * 2. Flexible period options that handle year rollover (when current period is 13, shows periods 1-3 from next year)
 * 3. Shows only enabled period numbers based on current period/week and selected year
 * 4. Shows only enabled week numbers - filters out weeks based on past dates and existing schedules
 * 5. Validates against existing schedules using direct period/week/year comparison
 * 6. Shows appropriate error messages and disables the create button when validation fails
 * 7. Displays favorite options from existing favorited schedules (only shows if favorites exist)
 * 8. Year-aware filtering - considers schedules from the selected year
 */

export interface DLOCreateScheduleModalProps {
  visible: boolean;
  onClose: () => void;
  onCopySchedule: (data: CreateScheduleData) => void;
  isCreating?: boolean;
  currentPeriod?: number;
  currentWeek?: number;
  scheduleTableData?: any[];
  currentYear?: number;
  isCopyMode?: boolean;
}

export interface CreateScheduleData {
  period: DLODropdownOption | null;
  week: DLODropdownOption | null;
  year: DLODropdownOption | null;
  favorite: DLODropdownOption | null;
}

const DLOCreateScheduleModal: React.FC<DLOCreateScheduleModalProps> = ({
  visible,
  onClose,
  onCopySchedule,
  isCreating = false,
  currentPeriod = 10,
  currentWeek = 2,
  scheduleTableData = [],
  currentYear = new Date().getFullYear(),
  isCopyMode = false,
}) => {
  const [formData, setFormData] = useState<CreateScheduleData>({
    period: null,
    week: null,
    year: {
      id: `${currentYear}`,
      label: `${currentYear}`,
      value: `${currentYear}`,
    }, // Default to current year
    favorite: null,
  });

  const [validationError, setValidationError] = useState<string>('');

  // Helper function to check if a period/week combination is in the past
  const isPastPeriodWeek = (period: number, week: number): boolean => {
    // Simple comparison without week calculation
    if (period < currentPeriod) {
      return true;
    }
    if (period === currentPeriod && week < currentWeek) {
      return true;
    }
    return false;
  };

  // Helper function to check if a schedule already exists for the selected period/week/year
  const scheduleExists = (period: number, week: number, year: number = currentYear): boolean => {
    if (!scheduleTableData || scheduleTableData.length === 0) return false;
    
    return scheduleTableData.some((schedule: any) => {
      // Direct comparison including year
      const schedulePeriod = parseInt(schedule.period);
      const scheduleWeek = parseInt(schedule.week);
      const scheduleYear = parseInt(schedule.year);
      
      console.log(`Checking schedule: Period ${schedulePeriod} Week ${scheduleWeek} Year ${scheduleYear} against Period ${period} Week ${week} Year ${year}`);
      return schedulePeriod === period && scheduleWeek === week && scheduleYear === year;
    });
  };

  // Helper function to get the highest week number from current year's schedule data
  const getHighestWeekFromCurrentYear = (): number => {
    if (!scheduleTableData || scheduleTableData.length === 0) return 0;
    
    const currentYearSchedules = scheduleTableData.filter((schedule: any) => {
      const scheduleYear = parseInt(schedule.year);
      return scheduleYear === currentYear;
    });
    
    if (currentYearSchedules.length === 0) return 0;
    
    const highestWeek = Math.max(...currentYearSchedules.map((schedule: any) => parseInt(schedule.week)));
    console.log(`Highest week in current year (${currentYear}):`, highestWeek);
    return highestWeek;
  };

  // Generate period options - show current and next 3 periods, handling year rollover
  const maxPeriod = 13;
  const getPeriodOptionsForYear = (year: number): DLODropdownOption[] => {
    let periodsToShow: Array<{ period: number; year: number }> = [];
    
    if (year === currentYear) {
      // For current year, show current period and next periods
      for (let i = 0; i < 4; i++) {
        const periodNumber = currentPeriod + i;
        if (periodNumber <= maxPeriod) {
          periodsToShow.push({ period: periodNumber, year: currentYear });
        } else {
          // If we exceed max period, start from period 1 of next year
          periodsToShow.push({ period: periodNumber - maxPeriod, year: currentYear + 1 });
        }
      }
    } else if (year === currentYear + 1) {
      // For next year, show periods 1-3 (or up to 4 if current period is 13)
      const periodsFromNextYear = currentPeriod === maxPeriod ? 3 : Math.max(0, 4 - (maxPeriod - currentPeriod));
      for (let i = 1; i <= periodsFromNextYear; i++) {
        periodsToShow.push({ period: i, year: currentYear + 1 });
      }
    }
    
    // Filter to only show periods for the selected year
    const filteredPeriods = periodsToShow.filter(p => p.year === year);
    
    return filteredPeriods.map(({ period }) => ({
      id: `${period}`,
      label: `${period}`,
      value: `${period}`,
    }));
  };

  const periodOptions: DLODropdownOption[] = formData.year 
    ? getPeriodOptionsForYear(parseInt(formData.year.value))
    : getPeriodOptionsForYear(currentYear); // Show current year periods by default

  // Generate year options - current year and next year
  const yearOptions: DLODropdownOption[] = [
    {
      id: String(currentYear),
      label: String(currentYear),
      value: String(currentYear),
    },
    {
      id: String(currentYear + 1),
      label: String(currentYear + 1),
      value: String(currentYear + 1),
    },
  ];

  // Generate week options - always show all weeks (1-4), disable if already created, in the past, or is the current period/week
  const getUIWeekOptions = (): DLODropdownOption[] => {
    return getWeekOptions().map(option => {
      const weekNumber = parseInt(option.id);
      let isDisabled = false;
      if (formData.period && formData.year) {
        const selectedPeriod = parseInt(formData.period.value);
        const selectedYear = parseInt(formData.year.value);
        // Check if it's in the past (only for current year)
        const isPast = selectedYear === currentYear && isPastPeriodWeek(selectedPeriod, weekNumber);
        // Check if it's the current period and week (disable current period/week)
        const isCurrent = selectedYear === currentYear && selectedPeriod === currentPeriod && weekNumber === currentWeek;
        // Check if schedule already exists for this period/week/year
        const existsAlready = scheduleExists(selectedPeriod, weekNumber, selectedYear);
        isDisabled = isPast || isCurrent || existsAlready;
      }
      return {
        id: option.id,
        label: option.displayText,
        value: option.displayText,
        disabled: isDisabled,
      };
    });
  };

  // Generate favorite options from existing favorites in schedule table data
  const getFavoriteOptions = (): DLODropdownOption[] => {
    if (!scheduleTableData || scheduleTableData.length === 0) {
      // Return empty array if no schedule data
      return [];
    }

    // Extract favorites from schedule table data
    const favorites = scheduleTableData
      .filter((schedule: any) => schedule.isFavorite)
      .map((schedule: any, index: number) => {
        console.log('Processing favorite schedule:', schedule);
        // Use fav_name if available, otherwise fallback to period/week format
        let favoriteName = schedule.fav_name || `Period ${schedule.period} Week ${schedule.week}`;
        
        return {
          id: `fav-${schedule.id || index}`,
          label: favoriteName,
          value: `fav-${schedule.id || index}`,
        };
      });

    // Remove duplicates based on label
    const uniqueFavorites = favorites.filter((fav, index, arr) => 
      arr.findIndex(f => f.label === fav.label) === index
    );

    // If no favorites found, return empty array (don't show dropdown)
    if (uniqueFavorites.length === 0) {
      return [];
    }

    return uniqueFavorites;
  };

  const weekOptions = getUIWeekOptions();
  const favoriteOptions = getFavoriteOptions();

  const handleYearChange = (option: DLODropdownOption | DLODropdownOption[] | null) => {
    if (option && !Array.isArray(option)) {
      setFormData(prev => ({ ...prev, year: option, period: null, week: null })); // Reset period and week when year changes
      setValidationError(''); // Clear validation error when user makes changes
    }
  };

  const handlePeriodChange = (option: DLODropdownOption | DLODropdownOption[] | null) => {
    if (option && !Array.isArray(option)) {
      setFormData(prev => ({ ...prev, period: option, week: null })); // Reset week when period changes
      setValidationError(''); // Clear validation error when user makes changes
    }
  };

  const handleWeekChange = (option: DLODropdownOption | DLODropdownOption[] | null) => {
    if (option && !Array.isArray(option)) {
      setFormData(prev => ({ ...prev, week: option }));
      setValidationError(''); // Clear validation error when user makes changes
    }
  };

  const handleFavoriteChange = (option: DLODropdownOption | DLODropdownOption[] | null) => {
    if (option && !Array.isArray(option)) {
      setFormData(prev => ({ ...prev, favorite: option }));
    } else {
      // Handle clearing the favorite (when option is null)
      setFormData(prev => ({ ...prev, favorite: null }));
    }
  };

  const validateForm = (): boolean => {
    if (!formData.year || !formData.period || !formData.week) {
      return false;
    }

    const selectedYear = parseInt(formData.year.value);
    const selectedPeriod = parseInt(formData.period.value);
    const selectedWeek = parseInt(formData.week.value);

    // Check if the selected period/week is in the past (only for current year)
    if (selectedYear === currentYear && isPastPeriodWeek(selectedPeriod, selectedWeek)) {
      setValidationError('Cannot create schedule for past period/week');
      return false;
    }

    // Check if the selected period/week is the current period/week (only for current year)
    if (selectedYear === currentYear && selectedPeriod === currentPeriod && selectedWeek === currentWeek) {
      setValidationError('Cannot create schedule for current period/week');
      return false;
    }

    // Check if schedule already exists for the selected year
    if (scheduleExists(selectedPeriod, selectedWeek, selectedYear)) {
      setValidationError('The schedule for the selected period and week exists');
      return false;
    }

    setValidationError('');
    return true;
  };

  // Run validation whenever form data changes
  useEffect(() => {
    // Debug: Log the schedule table data to understand its structure
    if (scheduleTableData && scheduleTableData.length > 0) {
      console.log('Schedule Table Data in Modal:', scheduleTableData);
      scheduleTableData.forEach((schedule, index) => {
        console.log(`Schedule ${index}: Period ${schedule.period}, Week ${schedule.week}, Year ${schedule.year}, isFavorite: ${schedule.isFavorite}`);
      });
    }

    if (formData.year && formData.period && formData.week) {
      validateForm();
    } else {
      setValidationError('');
    }
  }, [formData.year, formData.period, formData.week, scheduleTableData, currentYear]);

  const handleCopyPress = () => {
    if (validateForm()) {
      onCopySchedule(formData);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      period: null,
      week: null,
      year: {
        id: `${currentYear}`,
        label: `${currentYear}`,
        value: `${currentYear}`,
      }, // Reset to current year
      favorite: null,
    });
    setValidationError('');
    onClose();
  };

  
  // Determine button text based on validation
  const getButtonText = (): string => {
    return formData.favorite || isCopyMode ? 'Copy Schedule' : 'Create Schedule';
  };

  // Button should be enabled when year, period and week are selected, regardless of validation errors
  const isButtonDisabled = !formData.year || !formData.period || !formData.week || isCreating;

  return (
    <DLOModal
      visible={visible}
      onClose={handleClose}
      title={getButtonText()}
      primaryButtonText={getButtonText()}
      secondaryButtonText="Cancel"
      onPrimaryPress={handleCopyPress}
      onSecondaryPress={handleClose}
      primaryButtonDisabled={isButtonDisabled}
      primaryButtonLoading={isCreating}
      maxWidth={500}
    >
      <View style={styles.formContainer}>
        {/* Period and Week Row */}
        <View style={styles.twoColumnRow}>
          {/* Period Dropdown */}
          <View style={styles.halfFieldContainer}>
            <View style={styles.labelRow}>
              <Text style={styles.label}>Period</Text>
              <Text style={styles.required}>*Required</Text>
            </View>
            <DLODropdown
              value={formData.period}
              options={periodOptions}
              disableSorting={true}
              onSelect={handlePeriodChange}
              showLabel={false}
              size="medium"
              key={`period-${formData.year?.value || 'none'}`} // Force re-render when year changes
            />
          </View>

          {/* Week Dropdown */}
          <View style={styles.halfFieldContainer}>
            <View style={styles.labelRow}>
              <Text style={styles.label}>Week</Text>
              <Text style={styles.required}>*Required</Text>
            </View>
            <DLODropdown
              value={formData.week}
              options={weekOptions}
              disableSorting={true}
              onSelect={handleWeekChange}
              showLabel={false}
              size="medium"
              key={`week-${formData.year?.value || 'none'}-${formData.period?.value || 'none'}`} // Force re-render when year or period changes
            />
          </View>
        </View>

        {/* Second Row: Favorite and Year (or just Year if no favorites) */}
        <View style={styles.twoColumnRow}>
          {/* Favorite Dropdown - Only show if there are favorites */}
          {favoriteOptions.length > 0 ? (
            <View style={styles.halfFieldContainer}>
              <Text style={styles.label}>Favorite</Text>
              <DLODropdown
                placeholder="Select a favorite schedule"
                value={formData.favorite}
                options={favoriteOptions}
                onSelect={handleFavoriteChange}
                showLabel={false}
                size="medium"
                isClearable={true}
              />
            </View>
          ) : null}

          {/* Year Dropdown */}
          <View style={favoriteOptions.length > 0 ? styles.halfFieldContainer : styles.fieldContainer}>
            <View style={styles.labelRow}>
              <Text style={styles.label}>Year</Text>
              <Text style={styles.required}>*Required</Text>
            </View>
            <DLODropdown
              value={formData.year}
              options={yearOptions}
              disableSorting={true}
              onSelect={handleYearChange}
              showLabel={false}
              size="medium"
            />
          </View>
        </View>

        {/* Validation Error Message */}
        {validationError && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>
              {validationError}
            </Text>
          </View>
        )}
      </View>
    </DLOModal>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    gap: 24,
    paddingVertical: 8,
  },
  twoColumnRow: {
    flexDirection: 'row',
    gap: 16,
  },
  fieldContainer: {
    gap: 8,
    width:'48%'
  },
  halfFieldContainer: {
    flex: 1,
    gap: 8,
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontSize: 14,
    fontWeight: '400',
    color: '#3A3A3A',
    lineHeight: 20,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  required: {
    fontSize: 12,
    fontWeight: '400',
    color: '#DC3545',
    lineHeight: 16,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  errorContainer: {
    marginTop: -8, // Bring it closer to the fields
    paddingHorizontal: 4,
  },
  errorText: {
    fontSize: 12,
    fontWeight: '400',
    color: '#DC3545',
    lineHeight: 16,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default DLOCreateScheduleModal;
